import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Skeleton } from "@/components/ui/skeleton";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { resolveCoachPhoto } from "@/lib/photoFallback";
import { Users, MapPin, Globe, Mail, Phone, Pencil, Award, Facebook, Instagram, CheckCircle2 } from "lucide-react";
import { PublicGallery, PublicAchievements } from "@/components/EntityEditShared";
import { PublicEntityLinks } from "@/components/EntityLinks";
import { EntityBadge } from "@/components/EntityBadge";
import { CoachPalmares } from "@/components/CoachPalmares";

export default function CoachDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [coach, setCoach] = useState(null);
  const [incoming, setIncoming] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/coaches/${slug}`);
        if (cancelled) return;
        setCoach(data);
        api.get(`/entity-links?target_type=coach&target_id=${data.id}&status=accepted&limit=200`)
          .then((r) => !cancelled && setIncoming(r.data?.items || []))
          .catch(() => {});
      } catch (e) {
        if (e?.response?.status === 404) navigate("/coachs", { replace: true });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [slug, navigate]);

  if (loading) return <div className="max-w-5xl mx-auto py-8 px-4 space-y-4"><Skeleton className="h-48 w-full" /><Skeleton className="h-24 w-full" /></div>;
  if (!coach) return null;

  const isOwner = user && (user.id === coach.owner_user_id || (user.roles || []).some((r) => ["admin", "copf"].includes(r)));
  // V11.11 — Fallback silhouette coach/encadrant si pas de photo réelle
  const photoUrl = resolveCoachPhoto(coach);

  return (
    <div className="max-w-5xl mx-auto py-6 px-4 space-y-5" data-testid="coach-detail-page">
      <div className="relative h-48 rounded-xl overflow-hidden bg-gradient-to-br from-[hsl(var(--aito-lagoon-2))] to-[hsl(var(--aito-black))]">
        {/* V11.13 — Cover réelle si disponible, sinon fallback bannière Coach AITO.PF */}
        <img
          src={coach.cover_url || "/branding/cover-coach-default.png"}
          alt=""
          className="object-cover w-full h-full opacity-50"
          onError={(e) => { e.currentTarget.src = "/branding/cover-coach-default.png"; }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
        <div className="absolute bottom-4 left-4 right-4 flex items-end justify-between gap-3 flex-wrap">
          <div className="flex items-end gap-3 min-w-0">
            <div className="h-20 w-20 rounded-full overflow-hidden bg-white border-2 border-background shadow-lg shrink-0">
              <img
                src={photoUrl}
                alt={coach.full_name}
                className="h-full w-full object-cover"
                onError={(e) => { e.currentTarget.src = "/branding/coach-default.png"; }}
              />
            </div>
            <div className="text-white min-w-0">
              <h1 className="font-display text-3xl font-bold drop-shadow" data-testid="coach-name">{coach.full_name}</h1>
              {coach.short_motto && <p className="text-sm opacity-90 italic">« {coach.short_motto} »</p>}
              <div className="flex items-center gap-2 mt-1 flex-wrap text-xs">
                {(coach.disciplines || []).map((d) => (
                  <Badge key={d} variant="secondary" className="capitalize">{d}</Badge>
                ))}
                {(coach.island || coach.commune) && (
                  <span className="flex items-center gap-0.5 opacity-90"><MapPin className="h-3 w-3" />{[coach.commune, coach.island].filter(Boolean).join(" · ")}</span>
                )}
                {coach.experience_years && <span className="opacity-90">{coach.experience_years} ans d'expérience</span>}
              </div>
            </div>
          </div>
          {isOwner && (
            <Link to={`/coachs/${coach.id}/edit`} data-testid="coach-edit-btn">
              <Button size="sm" className="bg-[hsl(var(--aito-lagoon))] hover:bg-[hsl(var(--aito-lagoon-2))] text-black">
                <Pencil className="h-3.5 w-3.5 mr-1.5" />Modifier
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* V9.13 — Tabs masqués si vides + EntityBadge unifié pour athlètes coachés */}
      {(() => {
        const athletes = incoming.filter((l) => l.source_type === "athlete");
        const hasBio = !!coach.bio;
        const hasDiplomas = (coach.diplomas || []).length > 0;
        const hasContact = coach.contact_email || coach.contact_phone || coach.website || coach.social_facebook || coach.social_instagram;
        const showOverview = hasBio || hasDiplomas || hasContact;
        const hasAchievements = (coach.achievements || []).length > 0;
        const hasGallery = (coach.gallery || []).length > 0;
        return (
          <Tabs defaultValue={athletes.length ? "team-palmares" : (showOverview ? "overview" : hasAchievements ? "palmares" : "gallery")}>
            <TabsList data-testid="coach-tabs">
              {showOverview && <TabsTrigger value="overview" data-testid="coach-tab-overview">Présentation</TabsTrigger>}
              {athletes.length > 0 && <TabsTrigger value="team-palmares" data-testid="coach-tab-team-palmares">Palmarès équipe</TabsTrigger>}
              {athletes.length > 0 && <TabsTrigger value="athletes" data-testid="coach-tab-athletes">Athlètes coachés ({athletes.length})</TabsTrigger>}
              {hasAchievements && <TabsTrigger value="palmares" data-testid="coach-tab-palmares">Palmarès personnel ({coach.achievements.length})</TabsTrigger>}
              {hasGallery && <TabsTrigger value="gallery" data-testid="coach-tab-gallery">Galerie ({coach.gallery.length})</TabsTrigger>}
            </TabsList>

            {athletes.length > 0 && (
              <TabsContent value="team-palmares" className="mt-4">
                <CoachPalmares coachId={coach.id} />
              </TabsContent>
            )}

            {showOverview && (
              <TabsContent value="overview" className="mt-4 space-y-4">
                {hasBio && (
                  <Card className="p-5">
                    <h2 className="font-display font-semibold mb-2">Biographie</h2>
                    <p className="text-sm whitespace-pre-wrap text-muted-foreground">{coach.bio}</p>
                  </Card>
                )}
                {(hasDiplomas || hasContact) && (
                  <div className="grid md:grid-cols-2 gap-4">
                    {hasDiplomas && (
                      <Card className="p-5 space-y-2">
                        <h3 className="font-display font-semibold text-sm flex items-center gap-1.5"><Award className="h-4 w-4" />Diplômes</h3>
                        <ul className="text-sm space-y-1">
                          {(coach.diplomas || []).map((d, idx) => (
                            <li key={idx} className="flex items-start gap-1.5"><CheckCircle2 className="h-3.5 w-3.5 text-[hsl(var(--aito-lagoon))] mt-0.5 shrink-0" />{d}</li>
                          ))}
                        </ul>
                      </Card>
                    )}
                    {hasContact && (
                      <Card className="p-5 space-y-2">
                        <h3 className="font-display font-semibold text-sm">Contact</h3>
                        {coach.contact_email && <a href={`mailto:${coach.contact_email}`} className="text-xs flex items-center gap-1.5 hover:underline"><Mail className="h-3.5 w-3.5 text-muted-foreground" />{coach.contact_email}</a>}
                        {coach.contact_phone && <p className="text-xs flex items-center gap-1.5"><Phone className="h-3.5 w-3.5 text-muted-foreground" />{coach.contact_phone}</p>}
                        {coach.website && <a href={coach.website} target="_blank" rel="noreferrer" className="text-xs flex items-center gap-1.5 hover:underline"><Globe className="h-3.5 w-3.5 text-muted-foreground" />{coach.website}</a>}
                        {(coach.social_facebook || coach.social_instagram) && (
                          <div className="flex items-center gap-2 pt-1">
                            {coach.social_facebook && <a href={coach.social_facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className="text-muted-foreground hover:text-foreground"><Facebook className="h-4 w-4" /></a>}
                            {coach.social_instagram && <a href={coach.social_instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="text-muted-foreground hover:text-foreground"><Instagram className="h-4 w-4" /></a>}
                          </div>
                        )}
                      </Card>
                    )}
                  </div>
                )}
                <PublicEntityLinks sourceType="coach" sourceId={coach.id} fallbackLegacy={null} />
              </TabsContent>
            )}

            {athletes.length > 0 && (
              <TabsContent value="athletes" className="mt-4">
                <Card className="p-5 flex flex-wrap gap-2">
                  {athletes.map((l) => (
                    <EntityBadge
                      key={l.id}
                      kind="athlete"
                      name={l.source_name || "Athlète"}
                      slug={l.source_slug || l.source_id}
                      image={l.source_image}
                      link_status="accepted"
                      size="sm"
                      variant="chip"
                    />
                  ))}
                </Card>
              </TabsContent>
            )}

            {hasAchievements && (
              <TabsContent value="palmares" className="mt-4">
                <Card className="p-5"><PublicAchievements items={coach.achievements} /></Card>
              </TabsContent>
            )}

            {hasGallery && (
              <TabsContent value="gallery" className="mt-4">
                <Card className="p-5"><PublicGallery items={coach.gallery} /></Card>
              </TabsContent>
            )}
          </Tabs>
        );
      })()}
    </div>
  );
}
